import { v4 as uuidv4 } from 'uuid';
import { getHttpClient } from './httpClientProvider';
import { use401Handler } from '@/composables/use401Handler';

// Shared token cache across all instances to prevent race conditions
let sharedCachedToken: string | null = null;
let sharedTokenPromise: Promise<string> | null = null;
let sharedTokenExpiration: number | null = null; // Token expiration timestamp in seconds
let sharedRefreshTimer: number | null = null; // Timer for automatic token refresh

// HTTP status codes that indicate transient errors (retry-able)
const TRANSIENT_ERROR_CODES = [502, 503, 504];

// Default configuration for timeouts and retries
const DEFAULT_TIMEOUT_MS = 30000; // 30 seconds
const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_INITIAL_RETRY_DELAY_MS = 1000; // 1 second
const DEFAULT_MAX_RETRY_DELAY_MS = 10000; // 10 seconds

interface JWTClientConfig {
    /** Request timeout in milliseconds (default: 30000) */
    timeoutMs?: number;
    /** Maximum number of retries for transient errors (default: 3) */
    maxRetries?: number;
    /** Initial delay before first retry in milliseconds (default: 1000) */
    initialRetryDelayMs?: number;
    /** Maximum delay between retries in milliseconds (default: 10000) */
    maxRetryDelayMs?: number;
}

interface JWTRequestOptions {
    params?: Record<string, string>;
    body?: any;
    headers?: Record<string, string>;
    onProgress?: (progress: number) => void;
    /** Override timeout for this specific request */
    timeoutMs?: number;
    /** Override max retries for this specific request */
    maxRetries?: number;
}

/**
 * JWT HTTP Client for API Gateway authentication
 * Automatically adds Bearer token headers to requests
 * Token cache is shared across all instances to prevent race conditions
 *
 * Features:
 * - Configurable request timeout (default: 30s)
 * - Automatic retry with exponential backoff for transient errors (502, 503, 504)
 * - Automatic token refresh on 401 errors
 */
export class JWTHttpClient {
    private baseUrl: string;
    private timeoutMs: number;
    private maxRetries: number;
    private initialRetryDelayMs: number;
    private maxRetryDelayMs: number;

    constructor(baseUrl: string, config: JWTClientConfig = {}) {
        this.baseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        this.timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
        this.maxRetries = config.maxRetries ?? DEFAULT_MAX_RETRIES;
        this.initialRetryDelayMs = config.initialRetryDelayMs ?? DEFAULT_INITIAL_RETRY_DELAY_MS;
        this.maxRetryDelayMs = config.maxRetryDelayMs ?? DEFAULT_MAX_RETRY_DELAY_MS;
    }

    /**
     * Calculate delay for exponential backoff with jitter
     * @param attempt - Current retry attempt (0-based)
     * @returns Delay in milliseconds
     */
    private calculateRetryDelay(attempt: number): number {
        // Exponential backoff: delay = initialDelay * 2^attempt
        const exponentialDelay = this.initialRetryDelayMs * Math.pow(2, attempt);
        // Add jitter (±25%) to prevent thundering herd
        const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
        const delay = Math.min(exponentialDelay + jitter, this.maxRetryDelayMs);
        return Math.round(delay);
    }

    /**
     * Check if an error is a transient error that should be retried
     */
    private isTransientError(status: number): boolean {
        return TRANSIENT_ERROR_CODES.includes(status);
    }

    /**
     * Sleep for a specified duration
     */
    private sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    /**
     * Get or refresh JWT token
     * Checks if token is expired or about to expire before returning
     */
    private async getToken(): Promise<string> {
        // If we already have a token request in progress, wait for it
        if (sharedTokenPromise) {
            return sharedTokenPromise;
        }

        // Check if we have a cached token and if it's still valid
        if (sharedCachedToken && sharedTokenExpiration) {
            const now = Math.floor(Date.now() / 1000);
            const timeUntilExpiry = sharedTokenExpiration - now;

            // If token is already expired, log error and clear it
            if (timeUntilExpiry <= 0) {
                console.error('JWT Token is expired, fetching new token');
                this.clearToken();
            }
            // If token expires in less than 2 minutes, refresh it proactively
            else if (timeUntilExpiry < 120) {
                console.warn(`JWT Token expires in ${timeUntilExpiry}s, refreshing proactively`);
                this.clearToken();
            } else {
                return sharedCachedToken;
            }
        }

        // Issue a new token
        sharedTokenPromise = this.issueNewToken();

        try {
            const token = await sharedTokenPromise;

            if (!token) {
                console.error('JWT Token issuance returned empty token');
                throw new Error('Empty JWT token received');
            }

            sharedCachedToken = token;

            // Extract and store expiration time
            this.extractAndStoreExpiration(token);

            // Schedule automatic refresh
            this.scheduleTokenRefresh();

            return token;
        } catch (error) {
            console.error('Failed to get JWT token:', error);
            throw error;
        } finally {
            sharedTokenPromise = null;
        }
    }

    /**
     * Issue a new JWT token
     */
    private async issueNewToken(): Promise<string> {
        try {
            // Import auth store dynamically to avoid circular dependency issues
            const { useAuthStore } = await import('@/stores/auth');
            const authStore = useAuthStore();
            const httpClient = getHttpClient(authStore);

            const data = await httpClient.get<{ success: boolean; token?: string; message?: string }>(
                'admin/v4/core/',
                { sAction: 'issueToken' }
            );

            if (data.success === true && data.token) {
                return data.token;
            }

            throw new Error(data.message || 'Failed to issue JWT token');
        } catch (error) {
            throw error;
        }
    }

    /**
     * Extract expiration time from JWT token and store it
     */
    private extractAndStoreExpiration(token: string): void {
        try {
            const parts = token.split('.');
            if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]));
                if (payload.exp) {
                    sharedTokenExpiration = payload.exp;
                }
            }
        } catch (error) {
            console.error('Failed to extract token expiration:', error);
        }
    }

    /**
     * Schedule automatic token refresh before expiration
     * Tokens expire in 30 minutes, we'll refresh after 25 minutes (5 min buffer)
     */
    private scheduleTokenRefresh(): void {
        // Clear any existing refresh timer
        if (sharedRefreshTimer) {
            clearTimeout(sharedRefreshTimer);
            sharedRefreshTimer = null;
        }

        if (!sharedTokenExpiration) {
            return;
        }

        const now = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = sharedTokenExpiration - now;

        // Refresh 5 minutes before expiration (or immediately if less than 5 min left)
        const refreshBuffer = 300; // 5 minutes in seconds
        const timeUntilRefresh = Math.max(0, timeUntilExpiry - refreshBuffer);

        sharedRefreshTimer = window.setTimeout(() => {
            this.clearToken();
            // Trigger a refresh by getting a new token
            this.getToken().catch((error) => {
                console.error('Failed to auto-refresh JWT token:', error);
            });
        }, timeUntilRefresh * 1000);
    }

    /**
     * Clear cached token (for retry scenarios)
     * Clears the shared cache used by all instances
     */
    private clearToken(): void {
        sharedCachedToken = null;
        sharedTokenPromise = null;
        sharedTokenExpiration = null;

        // Clear refresh timer
        if (sharedRefreshTimer) {
            clearTimeout(sharedRefreshTimer);
            sharedRefreshTimer = null;
        }
    }

    /**
     * Get common headers with JWT Bearer token
     */
    private async getCommonHeaders(): Promise<Record<string, string>> {
        const token = await this.getToken();

        if (!token) {
            console.error('Attempting to make request with invalid/missing JWT token');
            throw new Error('Invalid or missing JWT token');
        }

        const headers = {
            Authorization: `Bearer ${token}`,
            'x-request-id': uuidv4(),
            'content-type': 'application/json',
            accept: 'application/json',
        };

        return headers;
    }

    /**
     * Make HTTP request with JWT authentication
     * Includes timeout, automatic retry for transient errors, and 401 handling
     */
    private async request<T>(
        method: string,
        path: string,
        options: JWTRequestOptions = {},
        retryContext: { authRetry: boolean; transientRetryCount: number } = {
            authRetry: false,
            transientRetryCount: 0,
        }, 
        returnType: 'blob' | 'json' = 'json',
    ): Promise<T> {
        const requestTimeout = options.timeoutMs ?? this.timeoutMs;
        const maxRetries = options.maxRetries ?? this.maxRetries;

        // Build the query string if we have params
        let queryString = '';
        if (options.params) {
            const searchParams = new URLSearchParams();
            Object.entries(options.params).forEach(([key, value]) => {
                searchParams.append(key, value);
            });
            queryString = `?${searchParams.toString()}`;
        }

        let reqBody = options.body || undefined;

        // Handle different body types
        if (reqBody) {
            if (reqBody instanceof FormData) {
                // FormData will be handled differently
            } else if (reqBody instanceof URLSearchParams) {
                // URLSearchParams will be handled differently
            } else {
                reqBody = JSON.stringify(reqBody);
            }
        }

        // Combine base URL with path
        const fullUrl = `${this.baseUrl}/${path.replace(/^\//, '')}${queryString}`;
        // Get headers with JWT token
        const headers = await this.getCommonHeaders();

        // Merge with additional headers
        const finalHeaders = { ...headers, ...options.headers };

        // Adjust content-type for different body types
        if (reqBody instanceof FormData) {
            delete finalHeaders['content-type']; // Let browser set boundary
        } else if (reqBody instanceof URLSearchParams) {
            finalHeaders['content-type'] = 'application/x-www-form-urlencoded; charset=UTF-8';
        }

        // Create AbortController for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), requestTimeout);

        try {
            const response = await fetch(fullUrl, {
                method,
                headers: finalHeaders,
                body: reqBody,
                credentials: 'omit', // Don't include cookies for API Gateway
                signal: controller.signal,
            });

            // Clear the timeout since request completed
            clearTimeout(timeoutId);

            // Handle 401 responses by clearing token and retrying once
            if (response.status === 401 && !retryContext.authRetry) {
                console.debug('JWT 401 error - clearing token and retrying');
                const { notifyRefreshAttempt } = use401Handler();
                notifyRefreshAttempt();

                this.clearToken();
                return this.request<T>(method, path, options, {
                    ...retryContext,
                    authRetry: true,
                });
            }

            // If still 401 after retry, show notification and redirect to login
            if (response.status === 401 && retryContext.authRetry) {
                console.warn('JWT 401 error after retry - redirecting to login');
                const { handle401WithLogout } = use401Handler();
                handle401WithLogout('accessing API Gateway services').catch(console.error);

                // Import router dynamically to avoid circular dependencies
                import('@/router').then(({ default: router }) => {
                    router.push('/login');
                });
            }

            // Handle transient errors (502, 503, 504) with retry and exponential backoff
            if (this.isTransientError(response.status) && retryContext.transientRetryCount < maxRetries) {
                const delay = this.calculateRetryDelay(retryContext.transientRetryCount);
                console.warn(
                    `JWT request failed with ${response.status} (attempt ${retryContext.transientRetryCount + 1}/${maxRetries}). ` +
                        `Retrying in ${delay}ms... [${method} ${path}]`
                );

                await this.sleep(delay);
                return this.request<T>(method, path, options, {
                    ...retryContext,
                    transientRetryCount: retryContext.transientRetryCount + 1,
                });
            }

            if (!response.ok) {
                const errorBody = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, body: ${errorBody}`);
            }
            if (returnType === 'blob') {
                return (await response.blob()) as unknown as T;
            }
            return (await response.json());
         } catch (error) {
            // Clear timeout on error
            clearTimeout(timeoutId);

            // Handle timeout (AbortError)
            if (error instanceof Error && error.name === 'AbortError') {
                // Retry on timeout if we haven't exceeded max retries
                if (retryContext.transientRetryCount < maxRetries) {
                    const delay = this.calculateRetryDelay(retryContext.transientRetryCount);
                    console.warn(
                        `JWT request timed out after ${requestTimeout}ms (attempt ${retryContext.transientRetryCount + 1}/${maxRetries}). ` +
                            `Retrying in ${delay}ms... [${method} ${path}]`
                    );

                    await this.sleep(delay);
                    return this.request<T>(method, path, options, {
                        ...retryContext,
                        transientRetryCount: retryContext.transientRetryCount + 1,
                    });
                }
                throw new Error(
                    `Request timed out after ${requestTimeout}ms and ${maxRetries} retries [${method} ${path}]`
                );
            }

            // Handle network errors (fetch failures) with retry
            if (error instanceof TypeError && retryContext.transientRetryCount < maxRetries) {
                const delay = this.calculateRetryDelay(retryContext.transientRetryCount);
                console.warn(
                    `JWT request network error (attempt ${retryContext.transientRetryCount + 1}/${maxRetries}). ` +
                        `Retrying in ${delay}ms... [${method} ${path}]`
                );

                await this.sleep(delay);
                return this.request<T>(method, path, options, {
                    ...retryContext,
                    transientRetryCount: retryContext.transientRetryCount + 1,
                });
            }

            throw error;
        }
    }

    /**
     * GET request with JWT authentication
     */
    async get<T>(path: string, params?: Record<string, string>, headers?: Record<string, string>): Promise<T> {
        return this.request<T>('GET', path, { params, headers });
    }

       /**
     * GET request with JWT authentication
     */
       async getBlob<T>(path: string, params?: Record<string, string>, headers?: Record<string, string>): Promise<T> {
        return this.request<T>('GET', path, { params, headers }, {
            authRetry: false,
            transientRetryCount: 0,
        }, 'blob');
    }

    /**
     * POST request with JWT authentication
     */
    async post<T>(
        path: string,
        body: any,
        params?: Record<string, string>,
        headers?: Record<string, string>
    ): Promise<T> {
        return this.request<T>('POST', path, { body, params, headers });
    }

    /**
     * PUT request with JWT authentication
     */
    async put<T>(
        path: string,
        body: any,
        params?: Record<string, string>,
        headers?: Record<string, string>
    ): Promise<T> {
        return this.request<T>('PUT', path, { body, params, headers });
    }

    /**
     * PATCH request with JWT authentication
     */
    async patch<T>(
        path: string,
        body: any,
        params?: Record<string, string>,
        headers?: Record<string, string>
    ): Promise<T> {
        return this.request<T>('PATCH', path, { body, params, headers });
    }

    /**
     * DELETE request with JWT authentication
     */
    async delete<T>(path: string, params?: Record<string, string>, headers?: Record<string, string>): Promise<T> {
        return this.request<T>('DELETE', path, { params, headers });
    }

    /**
     * Public method to prefetch and cache the JWT token
     * This can be called during app initialization to warm the token cache
     * and prevent race conditions when multiple requests need tokens simultaneously
     *
     * @param retries - Number of retry attempts (default: 3)
     * @param retryDelay - Delay between retries in ms (default: 500)
     */
    async prefetchToken(retries: number = 3, retryDelay: number = 500): Promise<boolean> {
        let lastError: Error | null = null;

        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                await this.getToken();
                return true;
            } catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                console.error(`JWT Token prefetch attempt ${attempt}/${retries} failed:`, error);

                // If not the last attempt, wait before retrying
                if (attempt < retries) {
                    await new Promise((resolve) => setTimeout(resolve, retryDelay));
                }
            }
        }

        console.error(`JWT Token prefetch failed after ${retries} attempts:`, lastError);
        return false;
    }
}
